import { ExpandMore, Info, PhotoCamera } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AccordionSummaryProps,
  Alert,
  Button,
  FormHelperText,
  Link,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useHandleError } from "Hooks/useHandleError";
import { useHasCamera } from "Hooks/useHasCamera";
import { useSingleEffect } from "Hooks/useSingleEffect";
import { UserData } from "Services/api/auth/interfaces";
import { cbPdfUrl, getCbTransferInfo } from "Services/api/cb/cb";
import { conciliation } from "Services/api/dms/dms";
import {
  BankAccountFieldsSchema,
  newAccountFieldsInitialValues,
  NewBankAccountFields,
} from "Shared/BankAccountFields/BankAccountFields";
import FormikForm from "Shared/FormikForm/FormikForm";
import { FormikImageField } from "Shared/FormikImageField/FormikImageField";
import { numericConfig } from "Shared/FormikMoney/FormikMoney";
import FormikSubmitButton from "Shared/FormikSubmitButton/FormikSubmitButton";
import { ImageCaptureDialog } from "Shared/ImageCaptureDialog/ImageCaptureDialog";
import { Query } from "Shared/Query/Query";
import { SectionSubTitle, SectionTitle } from "Shared/styled";
import { GENERIC_ERROR_MESSAGE } from "Utils/constants";
import { toFormData } from "Utils/toFormData";
import { Formik, useField, useFormikContext } from "formik";
import { useSnackbar } from "notistack";
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from "react";
import { useNumericFormat } from "react-number-format";
import { Link as RouterLink } from "react-router-dom";
import * as Yup from "yup";

export function ConciliationForm({
  user: { full_name, conciliation: conciliationData },
  setUser,
}: {
  user: UserData;
  setUser: Dispatch<SetStateAction<UserData>>;
}) {
  const accountField = "toAccount";
  const bankAccountSchema = BankAccountFieldsSchema(accountField);
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Stack spacing={1}>
      <SectionTitle variant="h1">Conciliación</SectionTitle>
      <Alert severity="info" color="success" variant="outlined">
        Para completar su proceso de afiliación es necesario que:
        <ul style={{ margin: 0 }}>
          <li>Transfiera el monto total de apertura a una de nuestras cuentas</li>
          <li>Proporcione el comprobante de transferencia</li>
          <li>Proporcione la información de la cuenta personal que ha utilizado para realizar la transferencia</li>
        </ul>
      </Alert>
      <Formik
        initialValues={{
          [accountField]: "new",
          ...newAccountFieldsInitialValues(),
          transferReceipt: undefined,
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const response = await conciliation(toFormData(values));

            if (response.status === "success") {
              setUser(response.data);
              enqueueSnackbar(response.message, { variant: "success" });
            } else {
              for (const key in response.data) {
                const element = response.data[key];
                for (const msg of element) {
                  enqueueSnackbar(msg, { variant: "error" });
                }
              }
            }
          } catch (error) {
            enqueueSnackbar(GENERIC_ERROR_MESSAGE, { variant: "error" });
            console.error(error);
          }

          setSubmitting(false);
        }}
        validationSchema={bankAccountSchema.concat(
          Yup.object({
            transferReceipt: Yup.mixed().required("El comprobante de transferencia es requerido"),
          })
        )}
      >
        <FormikForm width="100%">
          <SectionSubTitle variant="h2">Información de transferencia</SectionSubTitle>
          <TransferAmount conciliation={conciliationData} />
          <Accounts />
          <TransferReceiptField />
          <SectionSubTitle variant="h2" sx={{ paddingBottom: 0 }}>
            Cuenta de origen
          </SectionSubTitle>

          <NewBankAccountFields accountField={accountField} />
          <FormHelperText
            sx={(theme) => ({ display: "flex", fontSize: "0.9rem", color: theme.palette.primary.main, mt: "5px" })}
          >
            <Info sx={{ mr: 1 }} />
            Asegurese que la cuenta utilizada este a su nombre ({full_name}).
          </FormHelperText>

          <FormikSubmitButton fullWidth variant="contained">
            Enviar
          </FormikSubmitButton>
        </FormikForm>
      </Formik>
    </Stack>
  );
}

const Summary = styled((props: AccordionSummaryProps) => (
  <AccordionSummary expandIcon={<ExpandMore htmlColor="#7c7c7c" />} {...props} />
))(({ theme }) => ({
  width: "fit-content",
  border: "1px solid",
  borderColor: theme.palette.primary.main,
  borderRadius: "4px",
}));

function Accounts() {
  const handleError = useHandleError();

  const result = useQuery({
    queryKey: [getCbTransferInfo.name],
    queryFn: async () => {
      try {
        const response = await getCbTransferInfo();
        return response.data;
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
  });

  return (
    <Query
      result={result}
      OnLoading={() => <></>}
      onError={() => <>Ha ocurrido un error al intentar obtener el teléfono</>}
      onSuccess={({ rnc, accounts }) => (
        <Accordion variant="outlined" sx={{ my: "5px", border: "0px", position: "static" }}>
          <Summary>
            <Typography
              component="span"
              sx={{ fontWeight: 700, fontSize: "14px", lineHeight: "25px", color: "#7c7c7c" }}
            >
              Cuentas de la Cooperativa
            </Typography>
          </Summary>
          <AccordionDetails>
            <Table sx={{ mt: "0px !important", border: "0px" }}>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2} sx={{ px: 0 }}>
                    <Stack spacing={1}>
                      <label htmlFor={"rnc"}> RNC de la Cooperativa</label>
                      <Typography fontWeight={500}>{rnc}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell colSpan={2} sx={{ px: 0, textAlign: "right" }}>
                    <Button variant="outlined" download href={cbPdfUrl} fullWidth sx={{ width: "fit-content" }}>
                      DESCARGAR CUENTAS
                    </Button>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <Fragment key={account.institution + account.number}>
                    <TableRow sx={(theme) => ({ backgroundColor: theme.palette.primary.main, color: "white" })}>
                      <TableCell colSpan={4} sx={{ color: "white" }}>
                        <strong>{account.institution}</strong>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 0 }}>
                        <Stack>
                          <strong>NÚMERO</strong>
                          <>{account.number}</>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>
                        <Stack>
                          <strong>TIPO</strong>
                          <>{account.type}</>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ border: 0 }}>
                        <Stack>
                          <strong>MONEDA</strong>
                          <>{account.currency}</>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}
    />
  );
}

function TransferAmount({ conciliation }: { conciliation: UserData["conciliation"] }) {
  const { format } = useNumericFormat(numericConfig);
  const transferAmount = conciliation.initialFees.distribution.total[1];

  return (
    <Stack spacing={1}>
      <label htmlFor={"transferAmount"}>Monto a transferir</label>
      <Typography fontWeight={500}>RD$ {format(transferAmount.toString())}</Typography>
      <FormHelperText
        sx={(theme) => ({ display: "flex", fontSize: "0.9rem", color: theme.palette.primary.main, mt: "5px" })}
      >
        <Info sx={{ mr: 1 }} />
        <span>
          En caso de haber transferido una cantidad mayor a la indicada favor&nbsp;
          <Link component={RouterLink} to={"/contacto"}>
            contactarnos
          </Link>
        </span>
      </FormHelperText>
    </Stack>
  );
}

function TransferReceiptField() {
  const fieldName = "transferReceipt";
  const [hasCamera, setHasCamera] = useHasCamera();
  const [openDialog, setOpenDialog] = useState(false);
  const [{ value: image }, { error }, { setValue: setImage }] = useField<File | string | undefined>(fieldName);
  const [touched, setTouched] = useState(false);
  const { isSubmitting } = useFormikContext<{ [k: string]: string }>();

  useEffect(() => {
    if (isSubmitting) setTouched(true);
  }, [isSubmitting, setTouched]);

  useSingleEffect(() => {
    setHasCamera();
  });

  const spacing = hasCamera ? { spacing: 1 } : {};

  return (
    <>
      <SectionSubTitle variant="h2">Comprobante de transferencia</SectionSubTitle>
      <Stack direction="row" {...spacing}>
        {hasCamera && (
          <Button
            variant="outlined"
            startIcon={<PhotoCamera style={{ fontSize: "44px" }} />}
            onClick={() => setOpenDialog(true)}
            fullWidth
          >
            Tomar foto
          </Button>
        )}
        <FormikImageField name={fieldName} containerProps={{ spacing: 0, width: "100%" }} showHelperText={false} />
      </Stack>

      {typeof image === "string" ? (
        <FormHelperText>Comprobante.jpg</FormHelperText>
      ) : (
        <FormHelperText>
          {image
            ? image.name
            : "El archivo debe estar en formato jpg, png o pdf, y pesar menos de 6 MB. 1 solo archivo."}
        </FormHelperText>
      )}
      {touched && error && <FormHelperText error> {error}</FormHelperText>}

      <ImageCaptureDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onCapture={(imageSrc) => setImage(imageSrc)}
      />
    </>
  );
}
